/**
 * Device-local drafts for login / register / OTP — keyed by account type so flows do not
 * cross-pollinate. Never loaded from the backend.
 */

const LOGIN_ATTEMPT_PREFIX = 'ofrontend.loginLastAttempt.v1';
const REGISTER_ATTEMPT_PREFIX = 'ofrontend.registerLastAttempt.v1';
const OTP_LOGIN_ATTEMPT_PREFIX = 'ofrontend.otpLoginLastAttempt.v1';

export type LoginAttemptPayload = {
  identifier: string;
  password: string;
};

export type OtpLoginAttemptPayload = {
  email?: string;
  phone?: string;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function storageKey(prefix: string, option: string): string {
  return `${prefix}:${option.trim()}`;
}

function readJsonObject(key: string): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw?.trim()) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeJsonObject(key: string, value: Record<string, unknown>): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded, private browsing, etc. */
  }
}

function removeKey(key: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function pickString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function loadPersistedLoginAttempt(option: string): LoginAttemptPayload | null {
  const stored = readJsonObject(storageKey(LOGIN_ATTEMPT_PREFIX, option));
  if (!stored) {
    return null;
  }
  return {
    identifier: pickString(stored.identifier).trim(),
    password: pickString(stored.password),
  };
}

export function persistLoginAttempt(option: string, payload: LoginAttemptPayload): void {
  writeJsonObject(storageKey(LOGIN_ATTEMPT_PREFIX, option), {
    identifier: payload.identifier.trim(),
    password: payload.password,
  });
}

export function clearPersistedLoginAttempt(option: string): void {
  removeKey(storageKey(LOGIN_ATTEMPT_PREFIX, option));
}

const REGISTER_ATTEMPT_OMIT_KEYS = new Set(['logo', 'logo_url']);

export function sanitizeRegisterAttemptForStorage(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (REGISTER_ATTEMPT_OMIT_KEYS.has(key)) {
      continue;
    }
    out[key] = value;
  }
  return out;
}

export function mergeRegisterAttemptWithEmpty(
  emptyPayload: Record<string, unknown>,
  stored: Record<string, unknown> | null,
): Record<string, unknown> {
  if (!stored) {
    return JSON.parse(JSON.stringify(emptyPayload)) as Record<string, unknown>;
  }
  const sanitized = sanitizeRegisterAttemptForStorage(stored);
  return deepMergeRegisterDefaults(emptyPayload, sanitized);
}

function deepMergeRegisterDefaults(
  defaults: Record<string, unknown>,
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const out = JSON.parse(JSON.stringify(defaults)) as Record<string, unknown>;
  for (const [key, value] of Object.entries(stored)) {
    if (value === undefined) {
      continue;
    }
    const existing = out[key];
    if (isPlainObject(existing) && isPlainObject(value)) {
      out[key] = deepMergeRegisterDefaults(existing, value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

export function loadPersistedRegisterAttempt(option: string): Record<string, unknown> | null {
  return readJsonObject(storageKey(REGISTER_ATTEMPT_PREFIX, option));
}

export function persistRegisterAttempt(option: string, payload: Record<string, unknown>): void {
  writeJsonObject(
    storageKey(REGISTER_ATTEMPT_PREFIX, option),
    sanitizeRegisterAttemptForStorage(payload),
  );
}

export function clearPersistedRegisterAttempt(option: string): void {
  removeKey(storageKey(REGISTER_ATTEMPT_PREFIX, option));
}

export function loadPersistedOtpLoginAttempt(option: string): OtpLoginAttemptPayload | null {
  const stored = readJsonObject(storageKey(OTP_LOGIN_ATTEMPT_PREFIX, option));
  if (!stored) {
    return null;
  }
  const email = pickString(stored.email).trim();
  const phone = pickString(stored.phone).trim();
  if (!email && !phone) {
    return null;
  }
  return {
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
  };
}

export function persistOtpLoginAttempt(option: string, payload: OtpLoginAttemptPayload): void {
  const body: Record<string, unknown> = {};
  const email = payload.email?.trim();
  const phone = payload.phone?.trim();
  if (email) {
    body.email = email;
  }
  if (phone) {
    body.phone = phone;
  }
  if (Object.keys(body).length === 0) {
    return;
  }
  writeJsonObject(storageKey(OTP_LOGIN_ATTEMPT_PREFIX, option), body);
}

export function clearPersistedOtpLoginAttempt(option: string): void {
  removeKey(storageKey(OTP_LOGIN_ATTEMPT_PREFIX, option));
}
